import { useState, useEffect, useRef, useCallback } from 'react';
import type { MenuItemProps } from '../Menu';
import Dropdown from './Dropdown';
import DropDownButton from './DropDownButton';
import type {
  AsyncMultiSelectDropdownProps,
  DropdownTriggerRenderProps,
} from './types';

const LOADING_ITEM: MenuItemProps = {
  id: '__loading__',
  label: 'Ładowanie...',
  disabled: true,
  showCheckbox: false,
};

const NO_RESULTS_ITEM: MenuItemProps = {
  id: '__no_results__',
  label: 'Nie znaleziono wyników.',
  disabled: true,
  showCheckbox: false,
};

export function AsyncMultiSelectDropdown({
  label,
  items,
  selectedIds,
  loading,
  loadingMore = false,
  onSelectionChange,
  onSearchChange,
  onScrollEnd,
  menuMaxVisibleItems = 6,
  disabled = false,
  toolTip = false,
}: AsyncMultiSelectDropdownProps) {
  const [selectedLabelMap, setSelectedLabelMap] = useState<Map<string, string>>(
    new Map()
  );
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const hasLoadedRef = useRef(false);
  if (items.length > 0) {
    hasLoadedRef.current = true;
  }

  // Populate label map for pre-selected IDs when items first load.
  // Guards with !updated.has(id) so existing entries are never overwritten by search results.
  useEffect(() => {
    if (items.length === 0) {
      return;
    }
    setSelectedLabelMap((prev) => {
      const updated = new Map(prev);
      let changed = false;
      selectedIds.forEach((id) => {
        if (!updated.has(id)) {
          const item = items.find((i) => i.id === id);
          if (item) {
            updated.set(id, item.label);
            changed = true;
          }
        }
      });
      return changed ? updated : prev;
    });
  }, [items, selectedIds]);

  const menuItems: MenuItemProps[] = items.map((item) => ({
    id: item.id,
    label: item.label,
  }));

  if (loadingMore || (hasLoadedRef.current && loading && items.length === 0)) {
    menuItems.push(LOADING_ITEM);
  } else if (
    !loading &&
    !loadingMore &&
    items.length === 0 &&
    internalSearchQuery
  ) {
    menuItems.push(NO_RESULTS_ITEM);
  }

  const selectedLabels = selectedIds
    .map((id) => selectedLabelMap.get(id))
    .filter((l): l is string => l !== undefined);

  const handleSearchChange = useCallback(
    (q: string) => {
      setInternalSearchQuery(q);
      onSearchChange(q);
    },
    [onSearchChange]
  );

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setInternalSearchQuery('');
    }
  }, []);

  if (!hasLoadedRef.current && selectedIds.length > 0) {
    return (
      <div className="animate-pulse w-full min-h-[56px] rounded-lg bg-[#E5E7E9]" />
    );
  }

  return (
    <Dropdown
      menuFullWidth
      menuMaxVisibleItems={menuMaxVisibleItems}
      items={menuItems}
      selectedId={selectedIds}
      showSearch
      showCheckboxes
      multiSelect
      disabled={disabled}
      toolTip={toolTip}
      onSearchChange={handleSearchChange}
      onOpenChange={handleOpenChange}
      {...(onScrollEnd ? { onScrollEnd } : {})}
      trigger={({
        open,
        toggle,
        disabled: triggerDisabled,
      }: DropdownTriggerRenderProps) => (
        <DropDownButton
          label={label}
          value={selectedLabels}
          open={open}
          toggle={toggle}
          disabled={triggerDisabled}
        />
      )}
      onItemClick={(id: string) => {
        if (id === '__loading__' || id === '__no_results__') {
          return;
        }
        if (selectedIds.includes(id)) {
          setSelectedLabelMap((prev) => {
            const updated = new Map(prev);
            updated.delete(id);
            return updated;
          });
          onSelectionChange(selectedIds.filter((s) => s !== id));
        } else {
          const item = items.find((i) => i.id === id);
          if (item) {
            setSelectedLabelMap((prev) => new Map(prev).set(id, item.label));
          }
          onSelectionChange([...selectedIds, id]);
        }
      }}
    />
  );
}
